import type { CashAccountBalance, CashLedgerRow } from '../api/cash';
import { formatAmount } from '../format/money';

/**
 * The pure half of matching a bank account against its statement (Cash & bank step 7,
 * `docs/Cash_Bank_Flow_Design.md` §3.5).
 *
 * <p>
 * <strong>The check is cumulative, and the server's.</strong> Everything earlier statements cleared
 * (`clearedSoFar`) plus what is ticked now has to come to the statement's balance exactly. This
 * module mirrors the rule so a clerk sees what is left over while ticking, never by a 400 — what it
 * must not do is decide the record: the server re-sums and refuses, and the book balance kept is the
 * one it froze.
 * </p>
 */

/** What a row did to the account: in is plus, out is minus. */
export function signedOf(row: Pick<CashLedgerRow, 'direction' | 'amount'>): number {
  return row.direction === 'In' ? row.amount : -row.amount;
}

/** Rupees and paise, so a float sum of typed figures compares the way the server's decimals do. */
function paise(value: number): number {
  return Math.round(value * 100) / 100;
}

/** The ticked rows, signed and summed. `ticks` maps a ledger row's id to the day it cleared. */
export function tickedTotal(
  rows: readonly CashLedgerRow[],
  ticks: Readonly<Record<number, string>>,
): number {
  return paise(rows.filter((row) => row.id in ticks).reduce((sum, row) => sum + signedOf(row), 0));
}

/**
 * What the statement holds that the ticked lines do not — zero when it balances.
 *
 * <p>
 * Negative: the bank shows less than the book's ticked lines, usually charges or a card fee, recorded
 * as an expense paid from this bank. Positive: it shows more — interest, or a receipt nobody wrote up.
 * </p>
 */
export function leftOver(statementBalance: number, clearedSoFar: number, ticked: number): number {
  return paise(statementBalance - (clearedSoFar + ticked));
}

/**
 * The day a ticked row is offered as having cleared: the day it was booked, or the statement's own
 * closing day if the book dated it later — the server refuses a clearing after the statement.
 */
export function defaultClearedOn(
  row: Pick<CashLedgerRow, 'occurredOn'>,
  statementDate: string,
): string {
  const booked = row.occurredOn.slice(0, 10);

  return statementDate !== '' && booked > statementDate ? statementDate : booked;
}

/** Only a bank is reconciled; a till or petty cash is counted. The count's rule, mirrored. */
export function reconcilableAccounts(
  accounts: readonly CashAccountBalance[] | undefined,
): CashAccountBalance[] {
  return accounts?.filter((account) => account.kind === 'Bank') ?? [];
}

/**
 * The one sentence standing between this page and Save, or null — one at a time, most-blocking
 * first, in the server's order (the count's convention).
 */
export function reconcileProblem(input: {
  statementDate: string;
  today: string;
  lastStatementDate: string | null;
  statementBalance: string;
  ticks: Readonly<Record<number, string>>;
  leftOver: number;
}): string | null {
  const { statementDate, today, lastStatementDate, statementBalance, ticks } = input;

  if (statementDate === '') {
    return 'Say which day the statement closes on.';
  }

  if (statementDate > today) {
    return 'A statement cannot close on a day that has not happened yet.';
  }

  if (lastStatementDate !== null && statementDate <= lastStatementDate.slice(0, 10)) {
    return 'This account is already reconciled to that day or later — statements are matched in order.';
  }

  if (statementBalance.trim() === '' || !Number.isFinite(Number(statementBalance))) {
    return 'Enter the closing balance the statement shows.';
  }

  const days = Object.values(ticks);

  if (days.some((day) => day === '')) {
    return 'Every ticked line needs the day the statement shows it.';
  }

  if (days.some((day) => day > statementDate)) {
    return 'A line cannot clear after the statement’s closing day.';
  }

  if (input.leftOver !== 0) {
    return leftOverSentence(input.leftOver);
  }

  return null;
}

/** What to do about a difference — never "adjust": each is a real movement with its own voucher. */
export function leftOverSentence(difference: number, currency = ''): string {
  const amount = `${currency}${formatAmount(Math.abs(difference))}`;

  return difference < 0
    ? `The statement shows ${amount} less than the ticked lines. If the bank took charges or a fee, record it under Expenses paid from this bank, then tick it.`
    : `The statement shows ${amount} more than the ticked lines. If it is interest, record it as Money in › Other income into this bank, then tick it.`;
}

/** The save button, with the statement it records. */
export function reconcileLabel(statementDate: string, formatDay: (iso: string) => string): string {
  return statementDate === '' ? 'Reconcile' : `Reconcile to ${formatDay(statementDate)}`;
}
